/**
 * Videos Page - Video Generation Environment
 * Dedicated interface for video generation with Google Veo 3 support
 * Validates: Requirements 17.1, 17.2, 17.3, 17.5, 17.6
 */
import { useEffect, useState } from 'react';
import { useVideoStore } from '../stores/videoStore';
import {
  VideoModelSelector,
  VideoSettings,
  AudioTimingEditor,
  VideoPromptInput,
  VideoHistory,
  VideoPlayer,
} from '../components/video';

export function VideosPage() {
  const [showSettings, setShowSettings] = useState(true);

  const {
    jobs,
    currentJob,
    jobsLoading,
    providers,
    providersLoading,
    isGenerating,
    error,
    loadProviders,
    loadJobs,
    loadJob,
    generateVideo,
    clearError,
  } = useVideoStore();

  // Load providers and jobs on mount
  useEffect(() => {
    loadProviders();
    loadJobs();
  }, [loadProviders, loadJobs]);

  // Handle video generation
  const handleGenerate = async (prompt: string) => {
    await generateVideo(prompt);
  };

  // Handle job selection
  const handleSelectJob = (jobId: string) => {
    loadJob(jobId);
  };

  // Handle close preview
  const handleClosePreview = () => {
    useVideoStore.setState({ currentJob: null });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <h1 className="text-lg font-semibold text-white">Video Generation</h1>
          </div>

          {/* Model Selector */}
          {!providersLoading && providers.length > 0 && (
            <VideoModelSelector disabled={isGenerating} />
          )}
        </div>

        {/* Settings Toggle */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`p-2 rounded-lg transition-colors ${
            showSettings
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
          title={showSettings ? 'Hide settings' : 'Show settings'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="px-4 py-3 bg-red-900/50 border-b border-red-800 flex items-center justify-between">
          <p className="text-sm text-red-200">{error}</p>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto p-4 space-y-6">
          {/* Loading State */}
          {providersLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="w-8 h-8 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">No Video Providers Available</h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Please add your Google API key in settings to enable video generation with Veo 3.
              </p>
            </div>
          ) : (
            <>
              {/* Settings Panel */}
              {showSettings && (
                <div className="space-y-4">
                  <VideoSettings disabled={isGenerating} />
                  <AudioTimingEditor disabled={isGenerating} />
                </div>
              )}

              {/* Prompt Input */}
              <VideoPromptInput
                onSubmit={handleGenerate}
                disabled={isGenerating}
              />

              {/* Current Job Preview */}
              {currentJob && (
                <VideoPlayer
                  job={currentJob}
                  onClose={handleClosePreview}
                />
              )}

              {/* Generation History */}
              <VideoHistory
                jobs={jobs}
                currentJobId={currentJob?.id || null}
                isLoading={jobsLoading}
                onSelectJob={handleSelectJob}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
